import JSZip from 'jszip';
import { getToken } from '@/utils';
import type { Order } from '@shared/types';

/**
 * Fetch a single file's bytes through the backend proxy (handles auth + CORS).
 */
const fetchFileBlob = async (signedUrl: string): Promise<Blob> => {
  const token = getToken();
  const proxyUrl = `${import.meta.env.VITE_API_URL}/proxy-print?url=${encodeURIComponent(signedUrl)}`;
  const response = await fetch(proxyUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch file (HTTP status ${response.status})`);
  }
  return response.blob();
};

/** Trigger a browser "Save As" for an in-memory blob. */
const saveBlob = (blob: Blob, filename: string): void => {
  const blobUrl = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(blobUrl);
  }
};

/** Resolve the list of {name, url} files for an order, fetching signed URLs if needed. */
const resolveFiles = async (
  order: Order,
  signedFiles?: { name: string; url: string }[]
): Promise<{ name: string; url: string }[]> => {
  if (signedFiles) return signedFiles;
  const token = getToken();
  const res = await fetch(`${import.meta.env.VITE_API_URL}/order-urls/${order.shop_id}/${order.id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Failed to fetch file URLs');
  const data = await res.json();
  return (data.files || []).map((f: any) => ({ name: f.fileName, url: f.fileUrl }));
};

/**
 * Download each file in an order separately (one "Save As" per file). Files are
 * named `<phone>_file<n>_<name>` and spaced 500ms apart so browsers don't block
 * the rapid downloads. Throws on failure.
 */
export const downloadOrderFilesIndividually = async (
  order: Order,
  signedFiles?: { name: string; url: string }[]
): Promise<number> => {
  const files = await resolveFiles(order, signedFiles);
  if (files.length === 0) throw new Error('This order has no files to download.');

  const safePhone = order.customer_phone.replace(/\D/g, '');
  for (let i = 0; i < files.length; i++) {
    const blob = await fetchFileBlob(files[i].url);
    saveBlob(blob, `${safePhone}_file${i + 1}_${files[i].name}`);
    // Small delay so the browser doesn't block multiple sequential downloads.
    await new Promise((resolve) => setTimeout(resolve, 500));
  }
  return files.length;
};

/**
 * Download every file in an order, bundled into a single ZIP.
 *
 * Fetches fresh signed URLs for the order, pulls each file through the proxy in
 * parallel, zips them (numbered to preserve order and de-collide duplicate
 * names), and saves the archive. Throws on failure so callers can surface it.
 *
 * Pass `signedFiles` to skip the order-urls round-trip when URLs are already
 * loaded (e.g. the files modal already has them).
 */
export const downloadOrderAsZip = async (
  order: Order,
  signedFiles?: { name: string; url: string }[]
): Promise<number> => {
  const files = await resolveFiles(order, signedFiles);
  if (files.length === 0) {
    throw new Error('This order has no files to download.');
  }

  const zip = new JSZip();
  const seen: Record<string, number> = {};
  await Promise.all(
    files.map(async (file, i) => {
      const blob = await fetchFileBlob(file.url);
      let entryName = `${i + 1}_${file.name}`;
      if (seen[entryName] !== undefined) {
        seen[entryName] += 1;
        entryName = `${i + 1}_${seen[entryName]}_${file.name}`;
      } else {
        seen[entryName] = 0;
      }
      zip.file(entryName, blob);
    })
  );

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const safePhone = order.customer_phone.replace(/\D/g, '');
  saveBlob(zipBlob, `${safePhone}_order_${order.id.slice(0, 8)}.zip`);
  return files.length;
};
